import { useState, useEffect } from 'react'
import { Database, Clock, FileSpreadsheet, ChevronRight, Users, BookOpen, ChevronDown, Upload, Globe, Book } from 'lucide-react'
import { KycImportModal } from './components/KycImportModal'
import { DatabaseVisualization } from './components/DatabaseVisualization'
import { UnifiedSchemaDesign } from './components/UnifiedSchemaDesign'
import { TeamInsights } from './components/TeamInsights'
import { CountryGuides } from './components/CountryGuides'
import { VendorApiDocs } from './components/VendorApiDocs'
import { DATA_SOURCES_MANIFEST, CATEGORY_LABELS, getFreshness, getNextReviewDate } from './manifest'
import type { DataSourceEntry, Freshness } from './manifest'
import { coverageApi } from '@/api/client'

const FRESHNESS_CONFIG: Record<Freshness, { label: string; color: string; bgColor: string }> = {
  fresh: { label: 'Fresh', color: 'text-green-700 dark:text-green-400', bgColor: 'bg-green-100 dark:bg-green-500/10' },
  due_soon: { label: 'Due Soon', color: 'text-amber-700 dark:text-amber-400', bgColor: 'bg-amber-100 dark:bg-amber-500/10' },
  overdue: { label: 'Overdue', color: 'text-red-700 dark:text-red-400', bgColor: 'bg-red-100 dark:bg-red-500/10' },
  no_review: { label: 'No Review', color: 'text-[var(--text-muted)]', bgColor: 'bg-[var(--bg-tertiary)]' },
}

function FreshnessBadge({ freshness }: { freshness: Freshness }) {
  const config = FRESHNESS_CONFIG[freshness]
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${config.bgColor} ${config.color}`}>
      {config.label}
    </span>
  )
}

function CategoryBadge({ category }: { category: string }) {
  const label = CATEGORY_LABELS[category] || category
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-[var(--bg-tertiary)] text-[var(--text-secondary)]">
      {label}
    </span>
  )
}

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })
}

function formatBytes(bytes: number) {
  if (!bytes) return '—'
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / 1048576).toFixed(1)} MB`
}

// ─── Simple Markdown Renderer ──────────────────────────────────────────

function renderMarkdown(md: string) {
  const lines = md.split('\n')
  const elements: React.ReactNode[] = []
  let inTable = false
  let tableRows: string[][] = []
  let tableHeaders: string[] = []

  const flushTable = () => {
    if (tableHeaders.length === 0) return
    elements.push(
      <div key={`table-${elements.length}`} className="overflow-x-auto my-3">
        <table className="w-full text-xs border-collapse">
          <thead>
            <tr className="border-b border-[var(--border-color)]">
              {tableHeaders.map((h, i) => (
                <th key={i} className="text-left py-1.5 px-2 font-semibold text-[var(--text-secondary)] whitespace-nowrap">{h.trim()}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {tableRows.map((row, ri) => (
              <tr key={ri} className="border-b border-[var(--border-color)] hover:bg-[var(--bg-tertiary)]">
                {row.map((cell, ci) => (
                  <td key={ci} className={`py-1 px-2 ${cell.trim() === 'Y' ? 'text-green-500 font-bold' : cell.trim() === 'O' ? 'text-amber-500 font-bold' : 'text-[var(--text-muted)]'}`}>
                    {cell.trim() || '—'}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )
    tableHeaders = []
    tableRows = []
    inTable = false
  }

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]

    // Table row
    if (line.startsWith('|')) {
      const cells = line.split('|').slice(1, -1)
      // Separator row (|---|---|)
      if (cells.every(c => /^[\s-:]+$/.test(c))) continue
      if (!inTable) {
        inTable = true
        tableHeaders = cells.map(c => c.trim())
      } else {
        tableRows.push(cells.map(c => c.trim()))
      }
      continue
    }

    // End of table
    if (inTable) flushTable()

    // Headings
    if (line.startsWith('## ')) {
      elements.push(<h3 key={i} className="text-sm font-semibold text-[var(--text-primary)] mt-4 mb-2">{line.slice(3)}</h3>)
    } else if (line.startsWith('# ')) {
      elements.push(<h2 key={i} className="text-base font-bold text-[var(--text-primary)] mt-4 mb-2">{line.slice(2)}</h2>)
    } else if (line.startsWith('- **')) {
      const match = line.match(/^- \*\*(.+?)\*\*:?\s*(.*)$/)
      if (match) {
        elements.push(
          <div key={i} className="flex gap-2 text-xs ml-2 mb-1">
            <span className="font-semibold text-[var(--text-primary)] whitespace-nowrap">{match[1]}:</span>
            <span className="text-[var(--text-muted)]">{match[2]}</span>
          </div>
        )
      }
    } else if (line.startsWith('- ')) {
      elements.push(<p key={i} className="text-xs text-[var(--text-muted)] ml-2 mb-1">• {line.slice(2)}</p>)
    } else if (line.startsWith('*') && line.endsWith('*')) {
      elements.push(<p key={i} className="text-xs text-[var(--text-muted)] italic mb-2">{line.replace(/\*/g, '')}</p>)
    } else if (line.trim()) {
      elements.push(<p key={i} className="text-xs text-[var(--text-secondary)] mb-1">{line}</p>)
    }
  }
  if (inTable) flushTable()

  return elements
}

function DocumentationViewer({ content }: { content: string }) {
  const [expanded, setExpanded] = useState(false)

  return (
    <div className="rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-[var(--bg-tertiary)] transition-colors"
      >
        <h3 className="text-sm font-semibold text-[var(--text-primary)] flex items-center gap-2">
          <BookOpen size={16} />
          Documentation
        </h3>
        <ChevronDown size={16} className={`text-[var(--text-muted)] transition-transform ${expanded ? 'rotate-180' : ''}`} />
      </button>
      {expanded && (
        <div className="px-4 pb-4 border-t border-[var(--border-color)] max-h-[600px] overflow-y-auto">
          {renderMarkdown(content)}
        </div>
      )}
    </div>
  )
}

// ─── Detail View ───────────────────────────────────────────────────────

function DataSourceDetail({ entry, onBack }: { entry: DataSourceEntry; onBack: () => void }) {
  const [liveEntry, setLiveEntry] = useState(entry)
  const freshness = getFreshness(liveEntry)
  const nextReview = getNextReviewDate(liveEntry)
  const [importOpen, setImportOpen] = useState(false)

  useEffect(() => {
    if (entry.slug !== 'kyc-country-coverage') return
    coverageApi.stats().then(r => {
      if (!r.success || !r.data?.countries?.last_imported) return
      const importedAt = r.data.countries.last_imported
      const sourceFile = r.data.countries.source_file || entry.filename
      const importDate = new Date(importedAt + 'Z')
      const versionLabel = importDate.toLocaleDateString('en-AU', { month: 'short', year: 'numeric' })
      const lastUpdated = importDate.toISOString().split('T')[0]
      setLiveEntry({ ...entry, versionLabel, lastUpdated, filename: sourceFile })
    })
  }, [entry])

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3">
          <button onClick={onBack} className="mt-1 p-1 rounded hover:bg-[var(--bg-tertiary)] text-[var(--text-muted)]">
            <ChevronRight size={18} className="rotate-180" />
          </button>
          <div>
            <h2 className="text-xl font-semibold text-[var(--text-primary)]">{entry.displayName}</h2>
            <p className="text-sm text-[var(--text-muted)] mt-1">{entry.description}</p>
          </div>
        </div>
        {entry.slug === 'kyc-country-coverage' && (
          <button
            onClick={() => setImportOpen(true)}
            className="flex items-center gap-2 px-3 py-2 text-sm rounded-lg bg-teal-600 text-white hover:bg-teal-500 transition-colors flex-shrink-0"
          >
            <Upload size={14} />
            Import from XLSX
          </button>
        )}
      </div>

      {/* Metadata */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="p-4 rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)]">
          <p className="text-xs text-[var(--text-muted)] mb-1">Owner</p>
          <p className="text-sm font-medium text-[var(--text-primary)]">{entry.owner}</p>
        </div>
        <div className="p-4 rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)]">
          <p className="text-xs text-[var(--text-muted)] mb-1">Category</p>
          <CategoryBadge category={entry.category} />
        </div>
        <div className="p-4 rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)]">
          <p className="text-xs text-[var(--text-muted)] mb-1">Current Version</p>
          <p className="text-sm font-medium text-[var(--text-primary)]">{liveEntry.versionLabel}</p>
        </div>
        <div className="p-4 rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)]">
          <p className="text-xs text-[var(--text-muted)] mb-1">Status</p>
          <FreshnessBadge freshness={freshness} />
        </div>
      </div>

      {/* File info */}
      <div className="p-4 rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)]">
        <h3 className="text-sm font-semibold text-[var(--text-primary)] mb-3 flex items-center gap-2">
          <FileSpreadsheet size={16} />
          File Details
        </h3>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-xs text-[var(--text-muted)]">Filename</p>
            <p className="text-[var(--text-primary)] font-mono text-xs mt-0.5">{liveEntry.filename}</p>
          </div>
          <div>
            <p className="text-xs text-[var(--text-muted)]">File Size</p>
            <p className="text-[var(--text-primary)] mt-0.5">{formatBytes(entry.fileSizeBytes)}</p>
          </div>
          <div>
            <p className="text-xs text-[var(--text-muted)]">Last Updated</p>
            <p className="text-[var(--text-primary)] mt-0.5">{formatDate(liveEntry.lastUpdated)}</p>
          </div>
          <div>
            <p className="text-xs text-[var(--text-muted)]">Next Review Due</p>
            <p className="text-[var(--text-primary)] mt-0.5">{nextReview ? formatDate(nextReview) : 'No scheduled review'}</p>
          </div>
          <div>
            <p className="text-xs text-[var(--text-muted)]">Location</p>
            <p className="text-[var(--text-primary)] font-mono text-xs mt-0.5">docs/{entry.filePath}</p>
          </div>
        </div>
        {entry.notes && (
          <p className="text-xs text-[var(--text-muted)] mt-3 italic">{entry.notes}</p>
        )}
      </div>

      {/* Consumer Tools */}
      {entry.consumers.length > 0 && (
        <div className="p-4 rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)]">
          <h3 className="text-sm font-semibold text-[var(--text-primary)] mb-3 flex items-center gap-2">
            <Users size={16} />
            Used By
          </h3>
          <div className="space-y-2">
            {entry.consumers.map((c, idx) => (
              <div key={idx} className="flex items-center gap-3 text-sm">
                <span className="font-medium text-[var(--text-primary)]">{c.tool}</span>
                <span className="text-[var(--text-muted)]">— {c.usage}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Team Insights (only for Country Coverage) */}
      {entry.slug === 'kyc-country-coverage' && <TeamInsights />}

      {/* Database Visualization (only for Country Coverage) */}
      {entry.slug === 'kyc-country-coverage' && (
        <>
          <DatabaseVisualization />
          <UnifiedSchemaDesign />
        </>
      )}

      {/* Documentation */}
      {entry.documentation && (
        <DocumentationViewer content={entry.documentation} />
      )}

      {/* Import Modal */}
      {entry.slug === 'kyc-country-coverage' && (
        <KycImportModal
          isOpen={importOpen}
          onClose={() => setImportOpen(false)}
        />
      )}
    </div>
  )
}

// ─── Data Sources List (sub-tab) ──────────────────────────────────────

function DataSourcesList() {
  const [selectedSlug, setSelectedSlug] = useState<string | null>(null)
  const [filterCategory, setFilterCategory] = useState<string>('all')

  const sources = DATA_SOURCES_MANIFEST
  const selectedEntry = sources.find(s => s.slug === selectedSlug)

  if (selectedEntry) {
    return (
      <div className="max-w-5xl mx-auto">
        <DataSourceDetail entry={selectedEntry} onBack={() => setSelectedSlug(null)} />
      </div>
    )
  }

  const categories = ['all', ...new Set(sources.map(s => s.category))]
  const filtered = filterCategory === 'all' ? sources : sources.filter(s => s.category === filterCategory)

  const overdue = sources.filter(s => getFreshness(s) === 'overdue').length
  const dueSoon = sources.filter(s => getFreshness(s) === 'due_soon').length
  const fresh = sources.filter(s => getFreshness(s) === 'fresh').length

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      {/* Status Summary */}
      <div className="grid grid-cols-3 gap-4">
        <div className="p-4 text-center rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)]">
          <p className="text-2xl font-bold text-green-600 dark:text-green-400">{fresh}</p>
          <p className="text-xs text-[var(--text-muted)]">Fresh</p>
        </div>
        <div className="p-4 text-center rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)]">
          <p className="text-2xl font-bold text-amber-600 dark:text-amber-400">{dueSoon}</p>
          <p className="text-xs text-[var(--text-muted)]">Due Soon</p>
        </div>
        <div className="p-4 text-center rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)]">
          <p className="text-2xl font-bold text-red-600 dark:text-red-400">{overdue}</p>
          <p className="text-xs text-[var(--text-muted)]">Overdue</p>
        </div>
      </div>

      {/* Category Filter */}
      <div className="flex items-center gap-2 flex-wrap">
        {categories.map(cat => (
          <button
            key={cat}
            onClick={() => setFilterCategory(cat)}
            className={`px-3 py-1.5 text-xs rounded-full transition-colors ${
              filterCategory === cat
                ? 'bg-primary text-white'
                : 'bg-[var(--bg-tertiary)] text-[var(--text-secondary)] hover:bg-[var(--bg-secondary)]'
            }`}
          >
            {cat === 'all' ? 'All' : CATEGORY_LABELS[cat] || cat}
          </button>
        ))}
      </div>

      {/* Source List */}
      <div className="space-y-3">
        {filtered.map(ds => {
          const freshness = getFreshness(ds)
          const nextReview = getNextReviewDate(ds)
          return (
            <div
              key={ds.slug}
              className="p-4 rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] hover:border-primary/30 transition-colors cursor-pointer"
              onClick={() => setSelectedSlug(ds.slug)}
            >
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-1">
                    <FileSpreadsheet size={16} className="text-primary flex-shrink-0" />
                    <h3 className="text-sm font-semibold text-[var(--text-primary)] truncate">
                      {ds.displayName}
                    </h3>
                    <FreshnessBadge freshness={freshness} />
                    <CategoryBadge category={ds.category} />
                  </div>
                  <div className="flex items-center gap-4 ml-7 text-xs text-[var(--text-muted)]">
                    <span>Owner: {ds.owner}</span>
                    <span>Version: {ds.versionLabel}</span>
                    <span className="flex items-center gap-1">
                      <Clock size={12} />
                      {formatDate(ds.lastUpdated)}
                    </span>
                    {ds.fileSizeBytes > 0 && <span>{formatBytes(ds.fileSizeBytes)}</span>}
                    <span>{ds.consumers.length} tool{ds.consumers.length !== 1 ? 's' : ''}</span>
                    {nextReview && <span>Next review: {formatDate(nextReview)}</span>}
                  </div>
                </div>
                <ChevronRight size={18} className="text-[var(--text-muted)] flex-shrink-0" />
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ─── Main Page ─────────────────────────────────────────────────────────

type DataSourcesTab = 'sources' | 'country-guides' | 'vendor-api-docs'

export function DataSourcesPage() {
  const [tab, setTab] = useState<DataSourcesTab>('sources')

  const tabs: { key: DataSourcesTab; label: string; icon: React.ReactNode }[] = [
    { key: 'sources', label: 'Data Sources', icon: <Database size={14} /> },
    { key: 'country-guides', label: 'Country Guides', icon: <Globe size={14} /> },
    { key: 'vendor-api-docs', label: 'Vendor Docs', icon: <Book size={14} /> },
  ]

  return (
    <div className="space-y-6">
      {/* Tab Header */}
      <div className="flex items-center gap-6 border-b border-[var(--border-color)]">
        {tabs.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`flex items-center gap-2 pb-2 text-sm font-medium border-b-2 transition-colors ${
              tab === t.key
                ? 'border-primary text-primary'
                : 'border-transparent text-[var(--text-muted)] hover:text-[var(--text-secondary)]'
            }`}
          >
            {t.icon}
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'sources' && <DataSourcesList />}
      {tab === 'country-guides' && <CountryGuides />}
      {tab === 'vendor-api-docs' && <VendorApiDocs />}
    </div>
  )
}
